import React, { useCallback, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Col, Container, Row } from 'reactstrap';

import { FieldError } from 'site-modules/shared/components/field-error/field-error';
import { FormFieldMask } from 'site-modules/shared/components/form-field-mask/form-field-mask';
import { Link } from 'site-modules/shared/components/link/link';
import { ZipInput } from 'site-modules/shared/components/zip-input/zip-input';
import { VALIDATORS } from 'site-modules/shared/components/appraisal/trade-in-appraiser/utils/trade-in-appraiser-utils';
import { CONDITION_AND_MILEAGE } from 'site-modules/shared/constants/appraisal/appraisal';
import { MILEAGE_FIELD_MASK } from 'site-modules/shared/constants/form-field-masks';
import { numberWithCommasV2 } from 'site-modules/shared/utils/string';
import { AppraiserBreadcrumbs } from 'site-modules/appraiser/components/appraiser-breadcrumbs/appraiser-breadcrumbs';
import {
  MILEAGE_INPUT,
  getValidationErrorMessage,
  validateZipCode,
} from 'site-modules/shared/utils/appraiser/appraiser-validation-helper';
import { trackEmailValidation, trackSubmit } from './appraiser-renewal-utils';

import './appraiser-renewal.scss';

const formatMileage = mileage => (mileage || mileage === 0 ? numberWithCommasV2(Number(mileage)) : '');

const CREATIVE_ID = 'edm-entry-appraisal-page-renewal';
const SUBMIT_BTN_TEXT = "See Your Car's Value";
const VEHICLE_DETAIL_LABEL_CLASSES = 'vehicle-detail-label text-cool-gray-40 size-14 fw-bold mb-0_25 text-uppercase';
const useBrowserLayoutEffect = typeof window === 'undefined' ? React.useEffect : React.useLayoutEffect;

const VehicleDetails = ({ vinModsRecord = null, onEdit }) => {
  const vehicle = vinModsRecord?.vehicle;
  const [showAllOptions, setShowAllOptions] = useState(false);
  const [visibleOptionCount, setVisibleOptionCount] = useState(2);
  const optionListRef = useRef(null);
  const options = vehicle?.selectedOptions || [];

  const toggleShowAllOptions = useCallback(() => {
    setShowAllOptions(current => !current);
  }, []);

  // Show exactly two rows of options
  useBrowserLayoutEffect(() => {
    const list = optionListRef.current;
    if (!list) return undefined;

    const updateVisibleOptionCount = () => {
      const optionElements = Array.from(list.querySelectorAll('.vehicle-option'));
      const firstRowTop = optionElements[0]?.offsetTop;
      const secondRowTop = optionElements.find(option => option.offsetTop > firstRowTop)?.offsetTop;
      const count =
        secondRowTop === undefined
          ? optionElements.length
          : optionElements.filter(option => option.offsetTop <= secondRowTop).length;
      setVisibleOptionCount(count);
    };

    updateVisibleOptionCount();
    if (typeof ResizeObserver !== 'undefined') {
      const observer = new ResizeObserver(updateVisibleOptionCount);
      observer.observe(list);
      return () => observer.disconnect();
    }

    window.addEventListener('resize', updateVisibleOptionCount);
    return () => window.removeEventListener('resize', updateVisibleOptionCount);
  }, [options]);

  if (!vehicle) return null;

  const remainingOptions = Math.max(options.length - visibleOptionCount, 0);
  const exteriorColor = vehicle.colors?.exterior?.oemName || vehicle.exteriorColor;
  const interiorColor = vehicle.colors?.interior?.oemName || vehicle.interiorColor;
  const colors = [exteriorColor, interiorColor].filter(Boolean).join(' / ');
  const vehicleDetails = [
    { label: 'Style', value: vehicle.styleName },
    { label: 'VIN', value: vehicle.vin },
    { label: 'Mileage', value: formatMileage(vehicle.mileage) },
    { label: 'Condition', value: vehicle.condition },
    { label: 'Colors', value: colors, className: 'vehicle-detail-colors' },
  ];

  return (
    <section
      className="vehicle-details bg-cool-gray-90 rounded-24 text-cool-gray-10 mt-1_5 p-1 p-md-1_5"
      aria-labelledby="vehicle-details-heading"
    >
      <h2 id="vehicle-details-heading" className="mb-1 size-20">
        Need to change anything else?
      </h2>
      <div className="vehicle-details-row">
        {vehicleDetails.map(({ label, value, className }) => (
          <div key={label} className={`vehicle-detail ${className || 'border-bottom border-cool-gray-70'} pb-1`}>
            <div className={VEHICLE_DETAIL_LABEL_CLASSES}>{label}</div>
            <div>{value}</div>
          </div>
        ))}
        <div className="vehicle-detail pb-1">
          <div className={VEHICLE_DETAIL_LABEL_CLASSES}>Options</div>
          <div className="vehicle-options d-flex flex-wrap gap-0_5">
            {options.length ? (
              <>
                <ul
                  id="vehicle-options-list"
                  ref={optionListRef}
                  className={`vehicle-options-list list-unstyled d-flex gap-0_5 m-0 p-0 flex-wrap w-100${showAllOptions ? '' : ' is-collapsed'}`}
                >
                  {options.map((option, index) => (
                    <li
                      className="vehicle-option size-14 text-cool-gray-30 fw-medium py-0_25 px-0_75"
                      key={option.id || option.name || option.description}
                      aria-hidden={!showAllOptions && index >= visibleOptionCount}
                      style={{ visibility: !showAllOptions && index >= visibleOptionCount ? 'hidden' : undefined }}
                    >
                      {option.name || option.description}
                    </li>
                  ))}
                </ul>
                {remainingOptions > 0 && (
                  <button
                    className="vehicle-options-more"
                    type="button"
                    aria-controls="vehicle-options-list"
                    aria-expanded={showAllOptions}
                    onClick={toggleShowAllOptions}
                  >
                    {showAllOptions ? 'Show fewer options' : `+ ${remainingOptions} more`}
                  </button>
                )}
              </>
            ) : (
              <div>None</div>
            )}
          </div>
        </div>
      </div>
      <Button
        className="edit-vehicle-info text-transform-none mt-1_5 fw-bold size-16 d-flex align-items-center"
        color="blue-30"
        outline
        type="button"
        onClick={onEdit}
        data-tracking-id="start_new_appraisal"
      >
        Edit Vehicle Info <span className="icon-arrow-right3 ms-0_5" aria-hidden="true" />
      </Button>
    </section>
  );
};

VehicleDetails.propTypes = {
  onEdit: PropTypes.func.isRequired,
  vinModsRecord: PropTypes.shape({
    vehicle: PropTypes.shape({}),
  }),
};

const ConfirmDetails = ({
  userEmail = '',
  vehicleYear,
  vinModsRecord = null,
  zipCode: initialZipCode = '',
  isUnknownVisitorZip = false,
  onEdit,
  onSubmit,
}) => {
  const [formValues, setFormValues] = useState({
    mileage: String(vinModsRecord?.vehicle?.mileage ?? ''),
    zipCode: initialZipCode,
    email: userEmail,
  });
  const [errors, setErrors] = useState({});

  const updateField = useCallback((field, value) => {
    setFormValues(currentValues => ({ ...currentValues, [field]: value }));
    setErrors(currentErrors => ({ ...currentErrors, [field]: undefined }));
  }, []);

  const handleMileageChange = useCallback(value => updateField('mileage', value), [updateField]);
  const handleZipCodeChange = useCallback(value => updateField('zipCode', value), [updateField]);
  const handleEmailChange = useCallback(event => updateField('email', event.target.value), [updateField]);

  const handleSubmit = useCallback(
    event => {
      event.preventDefault();

      const nextErrors = {
        mileage: getValidationErrorMessage(CONDITION_AND_MILEAGE, MILEAGE_INPUT, formValues.mileage, {
          styleYear: vehicleYear,
        }),
        email: VALIDATORS.email.test(formValues.email) ? undefined : VALIDATORS.email.errorText,
        zipCode: validateZipCode(formValues.zipCode) ? undefined : 'Please enter a valid ZIP code.',
      };

      setErrors(nextErrors);

      trackEmailValidation(!nextErrors.email, CREATIVE_ID);

      if (Object.values(nextErrors).every(error => !error)) {
        const mileage = Number(formValues.mileage);
        const zipCode = formValues.zipCode;
        onSubmit({
          mileage,
          zipCode,
          email: formValues.email.trim(),
        });

        trackSubmit({ creativeId: CREATIVE_ID, ctaText: SUBMIT_BTN_TEXT, mileage, zipCode });
      }
    },
    [formValues, vehicleYear, onSubmit]
  );

  const mileageErrorId = 'renewal-mileage-error';
  const zipCodeErrorId = 'renewal-zip-code-error';
  const emailErrorId = 'renewal-email-error';

  return (
    <section className="confirm-details text-cool-gray-10" aria-labelledby="confirm-details-heading">
      <h2 id="confirm-details-heading" className="mb-0_25 size-24">
        Confirm your details
      </h2>
      <p className="size-16 mb-1 text-cool-gray-30">We&apos;ll send a copy of your appraisal report to your inbox.</p>
      <form noValidate onSubmit={handleSubmit}>
        <Row className="gy-1 gy-md-0 gx-lg-1_5 mb-1_25 mb-md-1">
          <Col xs={12} md={2}>
            <FormFieldMask
              id="renewal-mileage"
              name="mileage"
              inputMode="numeric"
              mask={MILEAGE_FIELD_MASK}
              label="Mileage"
              labelClassName="text-cool-gray-40 size-12 mb-0_5"
              inputClassName="size-16"
              defaultValue={formValues.mileage}
              onChange={handleMileageChange}
              isValid={!errors.mileage}
              ariaInvalid={!!errors.mileage}
              ariaDescribedBy={errors.mileage ? mileageErrorId : undefined}
              required
            />
            <FieldError id={mileageErrorId} error={errors.mileage} isLiveRegion classes="mt-0_5 renewal-field-error" />
          </Col>
          <Col xs={12} md={2}>
            <ZipInput
              id="renewal-zip-code"
              label="Zip code"
              inputAriaLabel="Zip code"
              labelClasses="form-label text-cool-gray-40 size-12 mb-0_5"
              inputClasses="form-control size-16"
              zipCode={formValues.zipCode}
              forceEmptyZip={isUnknownVisitorZip}
              onInputChange={handleZipCodeChange}
              inputProps={{
                'aria-invalid': !!errors.zipCode,
                'aria-describedby': errors.zipCode ? zipCodeErrorId : undefined,
              }}
            />
            <FieldError id={zipCodeErrorId} error={errors.zipCode} isLiveRegion classes="mt-0_5 renewal-field-error" />
          </Col>
          <Col xs={12} md={4}>
            <label className="form-label text-cool-gray-40 size-12 mb-0_5" htmlFor="renewal-email">
              Email Address
            </label>
            <input
              id="renewal-email"
              className="form-control size-16"
              type="email"
              autoComplete="email"
              placeholder="Enter email address"
              value={formValues.email}
              onChange={handleEmailChange}
              aria-invalid={!!errors.email}
              aria-describedby={errors.email ? emailErrorId : undefined}
              required
              data-tracking-id="provide_email"
              data-tracking-ignore-value
            />
            <FieldError id={emailErrorId} error={errors.email} isLiveRegion classes="mt-0_5 renewal-field-error" />
          </Col>
          <Col xs={12} md={4} className="renewal-submit-col align-self-start mt-1_25 mt-md-0">
            <Button className="w-100 text-transform-none mt-md-1_75 px-0" color="success" type="submit" size="lg">
              {SUBMIT_BTN_TEXT}
            </Button>
          </Col>
        </Row>
      </form>
      <p className="confirm-details-terms mb-0 mt-1_25 mt-md-0_5 text-cool-gray-40 size-12">
        * By clicking {SUBMIT_BTN_TEXT}, I accept the terms of the{' '}
        <Link to="/about/visitor-agreement.html">Edmunds Visitor Agreement</Link>.{' '}
        <Link to="/about/privacy.html">Our collection and use of your personal information</Link>.
      </p>
      <VehicleDetails vinModsRecord={vinModsRecord} onEdit={onEdit} />
    </section>
  );
};

ConfirmDetails.propTypes = {
  userEmail: PropTypes.string,
  vehicleYear: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  vinModsRecord: PropTypes.shape({}),
  zipCode: PropTypes.string,
  isUnknownVisitorZip: PropTypes.bool,
  onEdit: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
};

export function AppraiserRenewal({
  estimatedVehicle = null,
  params,
  userEmail = '',
  vinModsRecord = null,
  zipCode = '',
  isUnknownVisitorZip = false,
  onEdit,
  onSubmit,
}) {
  if (!estimatedVehicle) return null;

  const vehicleTitle = `${estimatedVehicle.year} ${estimatedVehicle.makeName} ${estimatedVehicle.modelName}`;

  return (
    <main id="main-content" tabIndex={-1} className="appraiser-renewal" data-tracking-parent={CREATIVE_ID}>
      <div className="main-content-container overflow-hidden">
        <Container className="mb-3">
          <AppraiserBreadcrumbs
            estimatedVehicle={estimatedVehicle}
            params={params}
            className="px-0 mb-1_5 text-white text-shadow-contrast"
          />
          <div className="appraiser-renewal-header text-center text-white">
            <h1 className="size-30 fw-bold mb-0_25 text-white">Welcome back!</h1>
            <p className="size-20 mb-1_5">See what your {vehicleTitle} is worth today</p>
          </div>
          <div className="content bg-white rounded-24 p-1_5 p-md-2 mx-auto">
            <ConfirmDetails
              userEmail={userEmail}
              vehicleYear={estimatedVehicle.year}
              vinModsRecord={vinModsRecord}
              zipCode={zipCode}
              isUnknownVisitorZip={isUnknownVisitorZip}
              onEdit={onEdit}
              onSubmit={onSubmit}
            />
          </div>
        </Container>
      </div>
    </main>
  );
}

AppraiserRenewal.propTypes = {
  estimatedVehicle: PropTypes.shape({
    year: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    makeName: PropTypes.string.isRequired,
    makeSlug: PropTypes.string,
    modelName: PropTypes.string.isRequired,
    modelSlug: PropTypes.string,
    submodels: PropTypes.arrayOf(PropTypes.shape({})),
  }),
  params: PropTypes.shape({}).isRequired,
  userEmail: PropTypes.string,
  vinModsRecord: PropTypes.shape({}),
  zipCode: PropTypes.string,
  isUnknownVisitorZip: PropTypes.bool,
  onEdit: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
};
